import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Upload, Send, X, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CollaboratePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    instagram: '',
    workType: '',
    collaborationInterest: '',
    message: ''
  });
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const collaborationTypes = [
    'Tattoo collaboration (with illustrators, painters)',
    'Studio showcase (displaying artwork)',
    'Creative collaboration',
    'Guest artist spot',
    'Other'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(file => {
      const isValidType = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'].includes(file.type);
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
      return isValidType && isValidSize;
    });
    setFiles(prev => [...prev, ...validFiles].slice(0, 5));
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (files.length === 0) return [];
    
    const formDataUpload = new FormData();
    files.forEach(file => {
      formDataUpload.append('files', file);
    });

    try {
      setUploadProgress(50);
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadProgress(100);
      return response.data.files || [];
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload files');
      return [];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Upload files first (if any)
      let uploadedFiles = [];
      if (files.length > 0) {
        uploadedFiles = await uploadFiles();
      }
      
      // Generate file URLs
      const fileUrls = uploadedFiles.map(f => `${BACKEND_URL}${f.file_url}`);
      
      // Save collaboration to backend
      await axios.post(`${API}/collaborate`, {
        name: formData.name,
        portfolio_link: formData.instagram,
        work_type: formData.workType,
        collaboration_interest: formData.collaborationInterest,
        message: formData.message,
        file_urls: fileUrls
      });

      // Create WhatsApp message with file links
      let fileLinksText = '';
      if (fileUrls.length > 0) {
        fileLinksText = `

📎 *PORTFOLIO/REFERENCE FILES:*
${fileUrls.map((url, i) => `${i + 1}. ${url}`).join('\n')}`;
      }

      const message = `✨ *COLLABORATION INQUIRY - Vision Tattoo* ✨

👤 *CONTACT DETAILS*
• Name: ${formData.name}
• Instagram/Portfolio: ${formData.instagram}

🎨 *PROJECT DETAILS*
• Type of Work: ${formData.workType}
• Collaboration Interest: ${formData.collaborationInterest}

💬 *MESSAGE*
${formData.message || 'No message provided'}${fileLinksText}

Looking forward to collaborating! 🖤`;

      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/919793498889?text=${encodedMessage}`;
      
      toast.success('Submission saved! Opening WhatsApp...');
      
      // Use same approach as Quote Estimator - immediate redirect with fallback
      setIsSubmitting(false);
      setUploadProgress(0);
      
      // Method 1: Try window.open first
      const newWindow = window.open(whatsappUrl, '_blank');
      
      // Method 2: If popup blocked, use location.href
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        setTimeout(() => {
          window.location.href = whatsappUrl;
        }, 100);
      }
      
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit. Please try again.');
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  return (
    <div data-testid="collaborate-page" className="tattoo-bg-contact min-h-screen">
      <Header />

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="font-heading text-5xl md:text-7xl font-bold mb-6 leading-none">
            COLLABORATE<br />WITH US
          </h1>
          <p className="font-body text-lg md:text-xl max-w-2xl leading-relaxed mb-12">
            We're open to working with artists and creators across different mediums — from tattoo collaborations to showcasing work within our space.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="paper-texture p-8 md:p-12"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="font-heading text-lg mb-2 block">Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full border-2 border-black px-4 py-3 font-body focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Your name"
              />
            </div>

            <div>
              <label className="font-heading text-lg mb-2 block">Instagram / Portfolio Link *</label>
              <input
                type="text"
                name="instagram"
                value={formData.instagram}
                onChange={handleInputChange}
                required
                className="w-full border-2 border-black px-4 py-3 font-body focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="@yourusername or portfolio URL"
              />
            </div>

            <div>
              <label className="font-heading text-lg mb-2 block">Type of Work *</label>
              <input
                type="text"
                name="workType"
                value={formData.workType}
                onChange={handleInputChange}
                required
                className="w-full border-2 border-black px-4 py-3 font-body focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="e.g., Illustrator, Painter, Tattoo Artist"
              />
            </div>

            <div>
              <label className="font-heading text-lg mb-2 block">Collaboration Interest *</label>
              <select
                name="collaborationInterest"
                value={formData.collaborationInterest}
                onChange={handleInputChange}
                required
                className="w-full border-2 border-black px-4 py-3 font-body focus:outline-none focus:ring-2 focus:ring-black bg-white"
              >
                <option value="">Select collaboration type</option>
                {collaborationTypes.map((type, index) => (
                  <option key={index} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="font-heading text-lg mb-2 block">Message</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                className="w-full border-2 border-black px-4 py-3 font-body focus:outline-none focus:ring-2 focus:ring-black resize-none"
                placeholder="Tell us about your work and collaboration ideas..."
              />
            </div>

            <div>
              <label className="font-heading text-lg mb-2 block">Upload Portfolio/Reference (Optional)</label>
              <p className="font-body text-sm text-gray-600 mb-3">
                Upload images or PDF of your work (max 5 files, 10MB each)
              </p>
              <div className="border-2 border-dashed border-gray-400 p-6 text-center hover:border-black transition-colors">
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  data-testid="file-upload-input"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload size={32} className="mx-auto mb-2 text-gray-500" />
                  <p className="font-body text-sm text-gray-600">Click to upload images or PDF</p>
                </label>
              </div>
              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  {files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-100 px-4 py-2" data-testid={`uploaded-file-${index}`}>
                      <span className="font-body text-sm truncate flex-1">{file.name}</span>
                      <span className="font-mono text-xs text-gray-500 mx-2">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-600 hover:text-red-800 p-1"
                        data-testid={`remove-file-${index}`}
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="mt-4">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-black transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="font-mono text-xs text-gray-600 mt-1">Uploading files...</p>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-black text-white py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="submit-collaboration-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Submit & Connect on WhatsApp
                </>
              )}
            </button>
          </form>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default CollaboratePage;
